import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditAgendaItemTextContentDto, FetchError, GetAgendaItemTextContentDto } from "../../../types";
import { configuration } from "../../../../configuration";
import { useAccessToken } from "../../../use-access-token";
import { queryKeys } from "../../queryKeys";
import { useSignalR } from "../../../../hooks";
import { optimisticallyUpdateQueryData } from "../../utilities";

type Variables = {
  agendaStepGuid: string;
  text: string;
};

const useEditAgendaItemTextContentRequest = () => {
  const { getAccessToken } = useAccessToken();
  const queryClient = useQueryClient();

  async function editAgendaItemTextContentRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: EditAgendaItemTextContentDto = {
      text: variables.text,
    };

    const response = await fetch(`${configuration.apiRootUrl}/agenda-items/${variables.agendaStepGuid}/text-content`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (response.status === 403) {
      // Someone else has locked the content, refetch to update the UI to reflect the current state
      queryClient.refetchQueries(queryKeys.meetings.getAgendaItemTextContent(variables.agendaStepGuid));
    } else if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editAgendaItemTextContentRequest };
};

const useEditAgendaItemTextContent = () => {
  const { editAgendaItemTextContentRequest } = useEditAgendaItemTextContentRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editAgendaItemTextContentRequest,
    onMutate: async (variables) => {
      const cachedData = await optimisticallyUpdateQueryData<GetAgendaItemTextContentDto>(
        queryClient,
        queryKeys.meetings.getAgendaItemTextContent(variables.agendaStepGuid),
        (cachedData) => ({
          ...cachedData,
          text: variables.text,
        })
      );

      return { cachedData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchAgendaItemContent(variables.agendaStepGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.meetings.getAgendaItemTextContent(variables.agendaStepGuid),
        context?.cachedData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.meetings.getAgendaItemTextContent(variables.agendaStepGuid));
    },
  });
};

export { useEditAgendaItemTextContent };
